<template>
  <div class="start">

    <send-sample-dialog :samples="EXAMPLES" />

    <start-header
      :media="{ id: 'e0b050cc-8095-464d-a309-d9da2e8fcdd8', alt: 'Holiday card example', video: true }"
      :bg="{ src: 'https://cdn.ellacard.com/page_backgrounds/Festive Holiday Baking.jpg', color: '#926E4A' }"
      :to="{ name: 'catalog-category', params: { category: 'holiday-cards' }, hash: '#search' }"
    >
      <template v-slot:h1>Send custom holiday cards and invitations in style</template>
      <template v-slot:h2>Make your family's annual holiday card unforgettable with custom pictures and videos on unlimited pages</template>
    </start-header>

    <start-layer clip-waves-class="light-gradient" :offset=".75">

      <div class="actions">
        <v-btn class="btn-pop" x-large rounded dark color="ec-blue" :to="{ name: 'catalog-category', params: { category: 'holiday-cards' }, hash: '#search' }">Start a holiday card <v-icon class="mr-n1" v-html="mdiChevronRight" /></v-btn>
        <v-btn class="btn-pop" x-large rounded dark color="pink" :to="{ hash: '#demo-family' }">Try demo…</v-btn>
      </div>

      <div class="grid-alternate">

        <web-media
          class="hover-pop rounded white"
          aspect-ratio="16 / 9"
          id="b26481aa-55dd-41a4-8286-aeb72a63801e"
          alt="Customizing a holiday card"
          is-video
        />

        <div>
          <h4 class="mb-3"><v-icon v-html="mdiPencil" /> Customize everything</h4>
          <div>
            Use our design tools to add your own pages with your own flare, complete with confetti, music, and backgrounds.
          </div>
        </div>

        <web-media
          class="hover-pop rounded white"
          aspect-ratio="16 / 9"
          id="6a2c961b-310a-43cd-9192-f460338b6b7e"
          alt="Adding photos and videos to a holiday card"
          is-video
        />

        <div>
          <h4 class="mb-3"><v-icon v-html="mdiImageMultiple" /> Photos and videos</h4>
          <div>
            Show off your photos and videos from the past year, or use our video maker to create your own holiday-themed video, right in your browser.
          </div>
        </div>

        <!--
        <v-lazy style="aspect-ratio: 16 / 9;">
          <div class="relative fill-height hover-pop">
          </div>
        </v-lazy>

        <div>
          <h3 class="text-h6 text-md-h5 header-font font-weight-bold mb-3">
            <v-icon class="mr-2" x-large v-html="mdiHomeCity" /> Remote signing
          </h3>
          <div class="text-md-h6">Is your family spread out? Create a group card to let everyone add photos, videos, and messages remotely.</div>
        </div>
        -->

        <web-media
          class="hover-pop rounded white"
          aspect-ratio="16 / 9"
          id="38f35100-dd02-4682-baab-d6781771ac02"
          alt="Example corporate holiday card opening"
          is-video
        />

        <div>
          <h4 class="mb-3"><v-icon v-html="mdiDomain" /> Corporate holiday cards</h4>
          <div>
            The easiest way to create and send beautiful and memorable corporate holiday cards. Use our <router-link :to="{ name: 'bulk-email' }">bulk email tool</router-link> to send to your entire mailing list.
          </div>
        </div>

        <div class="relative hover-pop-group" style="aspect-ratio: 1;">
          <div class="absolute rounded rn2 overflow-auto" style="width: 85%; top: 0; left: 0; max-height: 100%;">
            <web-media class="white" aspect-ratio="16 / 9" id="ef377a9f-b5cc-4736-9cf2-ee5c9055cd36" alt="Example guest list" />
          </div>
          <outline-phone class="absolute has-alpha r5" style="top: 10%; width: 45%; right: 0;" vertical>
            <web-media aspect-ratio="9 / 16" id="a23bb710-18e0-4d86-bcd9-484ed4d8192f" alt="Example holiday party RSVP on phone screen" />
          </outline-phone>
        </div>

        <div>
          <h4 class="mb-3"><v-icon v-html="mdiCalendar" /> Holiday party invitations</h4>
          <div>
            Use all of Ellacard's design tools to create a memorable invitation for your holiday party. Track deliveries, collect RSVPs, and send updates / reminders.
          </div>
        </div>

        <v-lazy style="aspect-ratio: 4 / 3;">
          <router-link class="hover-pop-group giftcards-pile" :to="{ name: 'start-gift-cards' }">
            <img class="rn5" style="top: 0; left: 0;" loading="lazy" src="https://gift.runa.io/static/product_assets/AMZ/AMZ-card.png" alt="Amazon gift card" />
            <img class="r3" style="top: 0; right: 5%;" loading="lazy" src="https://giftrocket-s3.imgix.net/Brands/US/Starbucks/Digital/starbucks_image_for_certificate.png" alt="Starbucks gift card" />
            <img class="rn3" style="top: 20%; left: 5%;" loading="lazy" src="https://gift.runa.io/static/product_assets/REI-US/REI-US-card.png" alt="REI gift card" />
            <img class="r3" style="bottom: 0; left: 0;" loading="lazy" src="https://giftrocket-s3.imgix.net/Brands/US/Target/Digital/Target.png" alt="Target gift card" />
            <img class="rn5" style="bottom: 0; right: 5%;" loading="lazy" src="https://gift.runa.io/static/product_assets/SEPHRA-US/SEPHRA-US-card.png" alt="Sephora gift card" />
            <img class="r1" style="top: 25%; right: 0;" loading="lazy" src="https://gift.runa.io/static/product_assets/UBER-US/UBER-US-card.png" alt="Uber gift card" />
          </router-link>
        </v-lazy>

        <div>
          <h4 class="mb-3"><v-icon v-html="mdiWalletGiftcard" /> Gift cards</h4>
          <div>
            Not going to see someone in person this year? Ellacard has <router-link :to="{ name: 'catalog-gift-cards' }">gift cards from thousands of international merchants</router-link>, all wrapped up neatly in your customized card.
          </div>
        </div>

      </div>

      <div class="actions">
        <v-btn class="btn-pop" x-large rounded dark color="ec-blue" :to="{ name: 'catalog-category', params: { category: 'holiday-cards' }, hash: '#search' }">Start a holiday card <v-icon class="mr-n1" v-html="mdiChevronRight" /></v-btn>
        <v-btn class="mx-6 btn-pop" x-large rounded dark color="pink" :to="{ hash: '#demo-family' }">Try demo…</v-btn>
      </div>

      <!-- Leave a spacer for the card shelf overlapping from the following component -->
      <div class="hidden-xs-only" style="height: min(3vw, 64px);" />

    </start-layer>

    <start-layer clip-waves-class="holiday-red" :offset=".9">
      <template v-slot:overflow>
        <div class="relative mx-auto" style="max-width: 1185px; z-index: 2;">
          <card-shelf class="absolute" style="top: calc(-32px - 3vw); pointer-events: none;" :examples="EXAMPLES.slice(0, 6)" />
        </div>
      </template>

      <!-- We need this spacer to leave room for the start-group-cards-header component -->
      <div class="mt-0" style="aspect-ratio: 5 / 7; width: min(calc(33% - 48px), 220px);" />

      <h2>Examples</h2>

      <div class="grid-alternate">
        <template v-for="ex in EXAMPLES">
          <v-lazy style="aspect-ratio: 4 / 3;">
            <router-link
              class="d-block relative hover-pop rounded-xl"
              style="aspect-ratio: 4 / 3;"
              v-bind:style="{ 'background-color': ex.color }"
              :to="{ hash: ex.hash }"
            >
              <card-background class="overflow-hidden" style="border-radius: 24px;" :src="ex.bg" />
              <start-preview
                class="absolute"
                style="pointer-events: none; top: 10%; left: 5%; width: 90%;"
                :alt="ex.title + ' example card'"
                :pages="ex.pages"
              />
            </router-link>
          </v-lazy>
          <div>
            <h4 class="mb-3">{{ex.title}}</h4>
            <div class="mb-6">{{ex.subtitle}}</div>
            <div class="ma-n2">
              <v-btn class="btn-pop ma-2" rounded dark color="ec-blue" :title="ex.title + ' demo'" :to="{ hash: ex.hash }">
                Try demo…
              </v-btn>
              <v-btn class="btn-pop ma-2" rounded dark color="ec-blue" :title="ex.title + 's'" :to="ex.to">More like this <v-icon class="mr-n1" v-html="mdiChevronRight" /></v-btn>
            </div>
          </div>
        </template>
      </div>

      <div class="actions">
        <v-btn class="btn-pop" x-large rounded dark color="ec-blue" :to="{ name: 'catalog-category', params: { category: 'holiday-cards' }, hash: '#search' }">
          All holiday cards <v-icon class="mr-n1" v-html="mdiChevronRight" />
        </v-btn>
        <v-btn class="btn-pop" x-large rounded dark color="ec-blue" :to="{ name: 'catalog-category', params: { category: 'holiday-party-invitations' }, hash: '#search' }">
          All holiday invitations <v-icon class="mr-n1" v-html="mdiChevronRight" />
        </v-btn>
      </div>
    </start-layer>

    <start-reviews tag="HO" />

    <start-footer />

  </div>
</template>


<script>
import CardBackground from '@/components/CardBackground.vue';
import ClipWaves from '@/components/ClipWaves.vue';
import OrgSignUpDialog from '@/components/OrgSignUpDialog.vue';
import OutlineLaptop from '@/components/OutlineLaptop.vue';
import OutlinePhone from '@/components/OutlinePhone.vue';
import SendSampleDialog from '@/components/SendSampleDialog.vue';
import CardShelf from '@/components/CardShelf.vue';
import StartFooter from '@/components/StartFooter.vue';
import StartHeader from '@/components/StartHeader.vue';
import StartLayer from '@/components/StartLayer.vue';
import StartPreview from '@/components/StartPreview.vue';
import StartReviews from '@/components/StartReviews.vue';
import WebMedia from '@/components/WebMedia.vue';
import { mdiPencil, mdiImageMultiple, mdiHomeCity, mdiDomain, mdiCalendar, mdiWalletGiftcard, mdiChevronRight } from '@mdi/js';


const EXAMPLES = [
  {
    id: '49a2a83f-ad3b-431a-a1b2-cfdb6ceb07fa',
    title: 'Family holiday card',
    pages: [
      {
        "jpg": "https://cdn.ellacard.com/r/76993ca9-7ac6-4855-b183-24358bd6fe66_270x378.jpg"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/20db8950-e604-4724-ab4a-11c19e2c3693_270x378.jpg"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/4094c72b-44c8-4799-b6f0-41de0fb919a9_270x378.jpg"
      }
    ],
    color: "#D9D0C7",
    bg: "https://cdn.ellacard.com/page_backgrounds/Outdoor Holly Snow.mp4",
    hash: '#demo-family',
    to: { name: 'catalog-category', params: { category: 'holiday-cards' }, hash: '#search' }
  },
  {
    id: '3d8d336c-0e1c-499d-8eb4-f203196073ff',
    title: 'Couple holiday card',
    pages: [
      {
        "webp": "https://cdn.ellacard.com/r/7ba8fd88-8368-4050-b570-769d4645701d_270x378.webp",
        "mp4": "https://cdn.ellacard.com/r/7ba8fd88-8368-4050-b570-769d4645701d_270x378.mp4"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/6c2f57b5-3541-4381-8092-cfde434510cc_270x378.jpg"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/814d0dff-0132-4914-b7e9-75ec8da2a8fb_270x378.jpg"
      }
    ],
    color: "#D49C7B",
    bg: "https://cdn.ellacard.com/page_backgrounds/Ornaments Green.jpg",
    hash: '#demo-couple',
    to: { name: 'catalog-category', params: { category: 'holiday-cards' }, hash: '#search' }
  },
  {
    id: '303d573d-3360-4f62-8a38-1a0711dd9abd',
    title: 'Corporate holiday card',
    pages: [
      {
        "jpg": "https://cdn.ellacard.com/r/a452fc7b-0709-4988-8c31-eb8f8f46e92a_270x378.jpg"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/4816c0d4-b579-4327-9c91-d7d0defe8fd0_270x378.jpg"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/8d2b35f2-e495-43e3-9c5d-e2173a375fef_270x378.jpg"
      }
    ],
    color: "#F9F7FA",
    bg: "https://cdn.ellacard.com/page_backgrounds/Corner Holly on White Background.jpg",
    hash: '#demo-corporate',
    to: { name: 'catalog-category', params: { category: 'holiday-cards' }, query: { q: 'Corporate' }, hash: '#search' }
  },
  {
    id: '1a33e7e1-3851-449c-9d6e-6da31487629a',
    title: 'Group holiday card',
    pages: [
      {
        "jpg": "https://cdn.ellacard.com/r/25d6d26e-a1cb-42c9-978a-66c204153b3a_270x378.jpg"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/795d7329-5739-4559-b927-5081ef0bea34_270x378.jpg"
      },
      {
        "webp": "https://cdn.ellacard.com/r/cd190739-b4ee-43c5-8861-a2156f2ec2a4_270x378.webp",
        "mp4": "https://cdn.ellacard.com/r/cd190739-b4ee-43c5-8861-a2156f2ec2a4_270x378.mp4"
      }
    ],
    color: "#926E4A",
    bg: "https://cdn.ellacard.com/page_backgrounds/Festive Holiday Baking.jpg",
    hash: '#demo-group',
    to: { name: 'catalog-category', params: { category: 'holiday-group-cards' }, hash: '#search' }
  },
  {
    id: '38afceeb-5a93-43cd-9e1d-edc444532ae1',
    title: 'Animated holiday card',
    pages: [
      {
        "webp": "https://cdn.ellacard.com/r/28eeabae-83b1-4778-9429-07285ba78f16_270x378.webp",
        "mp4": "https://cdn.ellacard.com/r/28eeabae-83b1-4778-9429-07285ba78f16_270x378.mp4"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/f3d554e2-4a84-4c01-915b-c2b52e670753_270x378.jpg"
      }
    ],
    color: "#A9B8CB",
    bg: "https://cdn.ellacard.com/page_backgrounds/Ornaments Blue.jpg",
    hash: '#demo-animated',
    to: { name: 'catalog-category', params: { category: 'holiday-cards' }, query: { q: 'Animated' }, hash: '#search' }
  },
  {
    id: '4a367736-fa24-4237-b695-ad10eeecdf3b',
    title: 'Christmas party invitation',
    pages: [
      {
        "jpg": "https://cdn.ellacard.com/r/cf556830-70a7-45f0-adfd-ccbe3684d594_270x378.jpg"
      },
      {
        "jpg": "https://cdn.ellacard.com/r/b073c310-a2f6-400b-b617-57d7009ac902_270x378.jpg"
      }
    ],
    color: "#FAF6EB",
    bg: "https://cdn.ellacard.com/page_backgrounds/Ornaments in Snow Bokeh.jpg",
    hash: '#demo-holiday-party',
    to: { name: 'catalog-category', params: { category: 'holiday-party-invitations' }, hash: '#search' }
  },
  {
    id: 'c4c340c7-9c06-437e-b77b-c3a467ff9219',
    title: 'Christmas cocktails invitation',
    pages: [
      {
        "jpg": "https://cdn.ellacard.com/r/dc95ab32-4ef8-4bf6-a68c-dc74a88187b9_270x378.jpg"
      },
      {
        "webp": "https://cdn.ellacard.com/r/e68db244-7dc3-4439-819a-76357ab428a1_270x378.webp",
        "mp4": "https://cdn.ellacard.com/r/e68db244-7dc3-4439-819a-76357ab428a1_270x378.mp4"
      }
    ],
    color: "#FF0C28",
    bg: "https://cdn.ellacard.com/page_backgrounds/Sparse Red.jpg",
    hash: '#demo-holiday-cocktails',
    to: { name: 'catalog-category', params: { category: 'holiday-party-invitations' }, hash: '#search' }
  }
];


export default {
  name: 'start-holiday',

  routerViewMeta: {
    $class: 'fade-transparent',
    $subtitle: 'Holiday cards and invitations',
    description: "Send custom holiday cards and invitations in style"
  },

  components: {
    CardBackground,
    ClipWaves,
    OrgSignUpDialog,
    OutlineLaptop,
    OutlinePhone,
    SendSampleDialog,
    CardShelf,
    StartFooter,
    StartHeader,
    StartLayer,
    StartPreview,
    StartReviews,
    WebMedia
  },

  data() {
    return {
      sample: null,
      showSendSample: false
    }
  },

  created() {
    Object.assign(this, { EXAMPLES, mdiPencil, mdiImageMultiple, mdiHomeCity, mdiDomain, mdiCalendar, mdiWalletGiftcard, mdiChevronRight });
  }
}
</script>
